<template>
  <v-container class="pa-0 fill-height d-flex flex-column">
    <v-row
      v-if="selectedQuestionnaireStore.reusingAnswers"
      style="width: 100%"
      class="flex-grow-0"
    >
      <v-col>
        <QuestionnaireMessageCard
          icon="icon-lightbulb-outline"
          @click="quickSubmit"
          :loading="isQuickSubmitting"
        >
          <template #title> Is this information correct?</template>
          If yes, you can skip editing and submit the questionnaire.
          <template #buttonText> Submit questionnaire</template>
        </QuestionnaireMessageCard>
      </v-col>
    </v-row>

    <v-row style="width: 100%" class="flex-grow-1">
      <v-col class="q-side-menu hidden-md-and-down mr-4">
        <SideMenu
          :items="sectionItems"
          :active-item="currentSectionIndex"
          :loading="isSavingSection"
          @new-section="(val) => goToSection(val)"
        />
      </v-col>

      <v-col>
        <StandardCardPageContainer :include-navigation="true">
          <template #title>{{ currentSet.title }}</template>
          <template #description>{{ currentSet.description }}</template>
          <span v-if="isSaving" class="saving-indicator"> Saving... </span>
          <QSection
            :set="currentSet"
            :context-id="contextId"
            :key="currentSectionIndex"
            @answer-updated="updateAnswerSet"
            @is-valid="setIsValid"
          />

          <template #backButton>
            <PActionButton
              v-if="!firstSection"
              class="me-auto"
              appkit-icon="icon-left-chevron-fill"
              :icon-size="'xs'"
              :prepend-icon="true"
              :variant="'outlined'"
              @click="goBack"
              >Back
            </PActionButton>
          </template>

          <template #saveButton>
            <PTextButton
              class="mr-2"
              :loading="isSavingDraft"
              :disabled="isSavingDraft"
              @click="saveDraft"
              >{{ lastSection ? "Save for later" : "Save" }}
            </PTextButton>
          </template>

          <template #nextButton>
            <PActionButton
              :append-icon="true"
              :loading="isSavingNext"
              :disabled="!isValid || isSavingNext"
              :icon-size="'xs'"
              appkit-icon="icon-right-chevron-fill"
              @click="goNext"
              >{{ lastSection ? "Submit" : "Next" }}
            </PActionButton>
          </template>
          <PModalBox
            :open-dialog="showWarning"
            :header="warningTitle"
            @close-dialog="showWarning = false"
          >
            <template #content>
              <div class="mb-5">
                {{ warningText }}
                <div class="pt-4 pb-4">
                  <div v-for="sectionTitle in incompleteSections">
                    <ul v-if="sectionTitle.length > 0" class="pl-8">
                      <li>
                        {{ sectionTitle }}
                      </li>
                    </ul>
                  </div>
                </div>
                {{ finalWarningText }}
              </div>
            </template>

            <template #rightbutton>
              <PPrimaryButton
                :loading="isLoadingContinueButton"
                @click="completeSubmitAction"
                >Continue
              </PPrimaryButton>
            </template>

            <template #leftbutton>
              <PSecondaryButton @click="showWarning = false"
                >Cancel
              </PSecondaryButton>
            </template>
          </PModalBox>
        </StandardCardPageContainer>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import QSection from "@/components/questionnaire/QSection.vue";
import SideMenu from "@/components/custom/QuestionnaireSideMenu.vue";
import { computed, ref, type Ref, onBeforeMount } from "vue";
import { AnswerStatus } from "@/models/enum/answerStatus";
import { saveAnswerSet, submitAnswerSet } from "@/services/answersService";
import { useSnackBarStore } from "@/stores/snackBarStore";
import type {
  QuestionnaireItemDto,
  QuestionnaireItemSetDto,
} from "@/models/dto/questionnaireItemSetDto";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { QuestionType } from "@/models/enum/questionType";
import { QuestionnaireSideMenuItem } from "@/models/questionnaireSideMenuItem";
import PTextButton from "@/components/custom/buttons/PTextButton.vue";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import { answerSetIsComplete } from "./questionnaireUtils";
import { onBeforeRouteLeave } from "vue-router";
import { RouterPath } from "@/models/enum/routerPath";
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import PModalBox from "../custom/PModalBox.vue";
import PPrimaryButton from "../custom/buttons/PPrimaryButton.vue";
import PSecondaryButton from "../custom/buttons/PSecondaryButton.vue";
import QuestionnaireMessageCard from "./QuestionnaireMessageCard.vue";

const emit = defineEmits<{
  (e: "submitted"): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const snackBarStore = useSnackBarStore();

const showWarning = ref(false);
const warningTitle = ref("");
const warningText = ref("");
const finalWarningText = ref("");
const isLoadingContinueButton = ref(false);

const language = ref(selectedQuestionnaireStore.language);
const invalidQuestions = ref(new Array<string>());
const isSavingNext = ref(false);
const isSavingDraft = ref(false);
const isSavingSection = ref(false);
const isQuickSubmitting = ref(false);
const isSaving = computed(
  () => isSavingNext.value || isSavingDraft.value || isSavingSection.value
);
const displayLeavePageWarning = computed(
  () => !isLeavePageWarningEnabled.value && hasUnsavedChanges.value
);
const hasUnsavedChanges = ref(false);
const isSubmitting = ref(false);
const itemSets: Ref<QuestionnaireItemSetDto[]> = ref([]);
const currentSectionIndex = ref(0);
const leavePage = ref(false);
const isLeavePageWarningEnabled = ref(false);

const isValid = computed(() => invalidQuestions.value.length === 0);
const contextId = computed(
  () => selectedQuestionnaireStore.questionnaire?.contextId ?? ""
);
const contextIds = computed(() =>
  selectedQuestionnaireStore.questionnaires.map((q) => q.contextId)
);
const firstSection = computed(() => currentSectionIndex.value === 0);
const lastSection = computed(
  () => currentSectionIndex.value === itemSets.value.length - 1
);

const sectionItems = computed(() =>
  itemSets.value.map(
    (item) => new QuestionnaireSideMenuItem(item.title, item.answerStatus)
  )
);

const incompleteSections = computed(() =>
  sectionItems.value.map((x) =>
    x.isSectionCompleted != AnswerStatus.Completed ? x.sectionTitle : ""
  )
);

const currentSet = computed(() => itemSets.value[currentSectionIndex.value]);

onBeforeMount(() => {
  itemSets.value = selectedQuestionnaireStore.itemSets;
});

onBeforeRouteLeave((to, from) => {
  selectedQuestionnaireStore.reusingAnswers = false;

  if (displayLeavePageWarning.value) {
    leavePage.value = window.confirm(
      "Are you sure you want to leave? Please make sure that the data in the current section is saved before leaving the page."
    );
    isLeavePageWarningEnabled.value = true;
    return leavePage.value;
  }

  if (!hasUnsavedChanges.value || to.path === RouterPath.SendQuestionnaire) {
    leavePage.value = true;
  }
  isLeavePageWarningEnabled.value = false;
  return leavePage.value;
});

function updateAnswerSet(value: object, qId: string): void {
  hasUnsavedChanges.value = true;
  for (const itemRow of currentSet.value.items) {
    for (const item of itemRow) {
      if (item.questionModel.question.id === qId) {
        item.answers = value;
        return;
      }
    }
  }
}

async function quickSubmit(): Promise<void> {
  isQuickSubmitting.value = true;
  try {
    await saveSection();
    submit();
  } catch (error: any) {
    showWarning.value = true;
    warningTitle.value = error.response.data[language.value].title;
    warningText.value = error.response.data[language.value].text;
    finalWarningText.value = error.response.data[language.value].finalText;
  } finally {
    isSavingNext.value = false;
    isSubmitting.value = false;
    isQuickSubmitting.value = false;
  }
}

async function goNext(): Promise<void> {
  isSavingNext.value = true;
  try {
    await saveSection();
    if (lastSection.value) {
      submit();
    } else {
      currentSectionIndex.value++;
      window.scrollTo(0, 0);
    }
  } catch (error: any) {
    showWarning.value = true;
    warningTitle.value = error.response.data[language.value].title;
    warningText.value = error.response.data[language.value].text;
    finalWarningText.value = error.response.data[language.value].finalText;
  } finally {
    isSavingNext.value = false;
    isSubmitting.value = false;
  }
}

function submit() {
  isSubmitting.value = true;
  snackBarStore.showSuccess("The questionnaire was successfully submitted");
  emit("submitted");
}

function goBack(): void {
  currentSectionIndex.value = currentSectionIndex.value - 1;
  window.scrollTo(0, 0);
}

async function goToSection(nextItemSetIndex: number) {
  isSavingSection.value = true;
  try {
    await saveSection();
  } catch (error: any) {
    snackBarStore.showError();
  } finally {
    isSavingSection.value = false;
    currentSectionIndex.value = nextItemSetIndex;
  }
}

async function saveSection(): Promise<void> {
  setAnswerStatus();
  currentSet.value.contextId = contextId.value;
  currentSet.value.contextIds = contextIds.value;
  (lastSection.value && !isSavingDraft.value && !isSavingSection.value) ||
  isQuickSubmitting.value
    ? await submitAnswerSet(currentSet.value)
    : await saveAnswerSet(currentSet.value);
  hasUnsavedChanges.value = false;
}

async function saveDraft(): Promise<void> {
  isSavingDraft.value = true;
  try {
    await saveSection();
    snackBarStore.showSuccess("The questionnaire was saved");
  } catch (error: any) {
    snackBarStore.showError();
  } finally {
    isSavingDraft.value = false;
  }
}

async function completeSubmitAction(): Promise<void> {
  isLoadingContinueButton.value = true;
  currentSet.value.submitWithNonCompletedQuestions = true;
  try {
    await saveSection();
    emit("submitted");
  } catch (error: any) {
    snackBarStore.showError();
  }
}

function setIsValid(isQuestionValid: boolean, invalidField: string): void {
  if (isQuestionValid) {
    invalidQuestions.value = invalidQuestions.value.filter(
      (id: string) => id !== invalidField
    );
  } else {
    const isAlready = invalidQuestions.value.includes(invalidField);
    if (!isAlready) {
      invalidQuestions.value.push(invalidField);
    }
  }
}

function setAnswerStatus() {
  const cleanedAnswerSet = removeNonRequiredQuestions();
  const isCompleted = answerSetIsComplete(cleanedAnswerSet);

  currentSet.value.answerStatus = isCompleted
    ? AnswerStatus.Completed
    : AnswerStatus.InProgress;
}

function removeNonRequiredQuestions(): QuestionnaireItemDto[] {
  const flattenSet = ([] as QuestionnaireItemDto[]).concat(
    ...currentSet.value.items
  );

  // Removing question types that are not mandatory to answer
  return flattenSet.filter(
    (obj) =>
      obj.questionModel.question.type !== QuestionType.Certificates &&
      obj.questionModel.question.type !== QuestionType.LeadText &&
      obj.questionModel.question.type !== QuestionType.KeyPersonnel &&
      obj.questionModel.question.type !== QuestionType.BeneficialOwners &&
      obj.questionModel.question.type !== QuestionType.TextArea &&
      obj.questionModel.question.type !== QuestionType.OwnershipIndividual &&
      obj.questionModel.question.type !== QuestionType.OwnershipCompany &&
      obj.questionModel.question.type !==
        QuestionType.PersonsWithPepAndSanctions &&
      obj.questionModel.question.type !==
        QuestionType.PersonWithPepAndSanctions &&
      obj.questionModel.question.type !== QuestionType.PersonsNameAndPosition &&
      obj.questionModel.question.id !== "Website" &&
      obj.questionModel.question.id !== "CountriesOfMainSuppliers"
  );
}
</script>

<style lang="scss">
.q-side-menu {
  max-width: 300px;
}

.saving-indicator {
  position: absolute;
  top: 2em;
  right: 3.5em;
}
</style>
